import { Link } from "react-router-dom";
import { clinicInfo } from "../data";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const treatmentLinks = [
    { label: "Rygbehandling", href: "/behandlinger/ryg" },
    { label: "Nakkesmerter", href: "/behandlinger/nakkesmerter" },
    { label: "Arme og ben", href: "/behandlinger/arme-og-ben" },
    { label: "Skulder", href: "/behandlinger/skulder" },
    { label: "Hofte", href: "/behandlinger/hofte" },
    { label: "Sportsskader", href: "/sportsskader" },
  ];

  const infoLinks = [
    { label: "Praktisk info & Priser", href: "/praktisk" },
    { label: "Kvalitetssikring", href: "/kvalitetssikring" },
    { label: "Evidens & Forskning", href: "/evidens" },
    { label: "Hvad er kiropraktik?", href: "/kiropraktik" },
    { label: "Vores personale", href: "/personale" },
    { label: "Kontakt os", href: "/kontakt" },
  ];

  return (
    <footer className="bg-brand-950 text-brand-100 border-t border-brand-900 pt-16 pb-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12">
          {/* Column 1: Brand & CVR & Socials (4 cols on lg) */}
          <div className="md:col-span-6 lg:col-span-4 space-y-6">
            <Link to="/" className="inline-block bg-white p-3 rounded-xl shadow-sm hover:scale-102 transition-transform">
              <img
                src="https://assets.auxodev.dk/assets/783a3379-7262-480b-b55e-1bf6fb783275/kiropraktisk-assens-logo-08_2.png"
                alt="Kiropraktisk Klinik Assens"
                className="h-10 w-auto object-contain"
              />
            </Link>
            <div className="space-y-3 text-xs text-brand-300">
              <p className="font-semibold text-white text-sm">{clinicInfo.name}</p>
              <p>CVR: {clinicInfo.cvr}</p>
              <p className="leading-relaxed">
                Klinikken har ydernummer, hvilket betyder, at den offentlige sygesikring automatisk yder ca. 20% tilskud til din behandling.
              </p>
            </div>
            {/* Social Links */}
            <div className="flex gap-3 pt-2">
              <a
                href={clinicInfo.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-900 text-brand-200 hover:bg-brand-600 hover:text-white transition-all cursor-pointer"
                aria-label="Facebook"
              >
                <i className="fa-brands fa-facebook-f text-sm" />
              </a>
              <a
                href={clinicInfo.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-900 text-brand-200 hover:bg-brand-600 hover:text-white transition-all cursor-pointer"
                aria-label="Instagram"
              >
                <i className="fa-brands fa-instagram text-sm" />
              </a>
            </div>
          </div>

          {/* Column 2: Behandlinger (4 cols on lg) */}
          <div className="md:col-span-3 lg:col-span-4 space-y-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white border-b border-brand-900 pb-2 font-display">
              Behandlinger
            </h3>
            <ul className="space-y-3 text-xs">
              {treatmentLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-brand-300 hover:text-white hover:translate-x-1 inline-flex items-center gap-1.5 transition-all duration-150"
                  >
                    <i className="fa-solid fa-chevron-right text-[8px] text-brand-500" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Information (4 cols on lg) */}
          <div className="md:col-span-3 lg:col-span-4 space-y-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white border-b border-brand-900 pb-2 font-display">
              Information
            </h3>
            <ul className="space-y-3 text-xs">
              {infoLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-brand-300 hover:text-white hover:translate-x-1 inline-flex items-center gap-1.5 transition-all duration-150"
                  >
                    <i className="fa-solid fa-chevron-right text-[8px] text-brand-500" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Åbningstider (6 cols on lg) */}
          <div className="md:col-span-6 lg:col-span-6 space-y-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white border-b border-brand-900 pb-2 font-display">
              Åbningstider
            </h3>
            <div className="space-y-2 text-xs text-brand-300">
              {clinicInfo.openingHours.map((item) => (
                <div key={item.day} className="flex justify-between border-b border-brand-900/30 pb-1">
                  <span>{item.day}</span>
                  <span className="text-white font-medium">{item.hours}</span>
                </div>
              ))}
              <div className="pt-2 text-[11px] text-brand-400 leading-relaxed">
                <span className="font-semibold text-brand-300 block">Telefontider:</span>
                {clinicInfo.telefontider}
              </div>
            </div>
          </div>

          {/* Column 5: Kontakt & Akut (6 cols on lg) */}
          <div className="md:col-span-6 lg:col-span-6 space-y-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white border-b border-brand-900 pb-2 font-display">
              Kontakt & Akut
            </h3>
            <div className="space-y-4 text-xs text-brand-300">
              <div className="flex items-start gap-2.5">
                <i className="fa-light fa-location-dot mt-0.5 text-brand-400 shrink-0 text-sm" />
                <p className="leading-relaxed">
                  <span className="font-semibold text-white block">Adresse:</span>
                  {clinicInfo.address}
                  <span className="block text-[10px] text-brand-400 mt-0.5">1. sal (elevator & gratis parkering)</span>
                </p>
              </div>
              <div className="flex items-center gap-2.5">
                <i className="fa-light fa-phone text-brand-400 shrink-0 text-sm" />
                <p>
                  <span className="font-semibold text-white mr-1">Tlf:</span>
                  <a href={`tel:${clinicInfo.phone}`} className="text-brand-200 hover:text-white hover:underline">
                    {clinicInfo.phone.replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4")}
                  </a>
                </p>
              </div>
              <div className="flex items-center gap-2.5">
                <i className="fa-light fa-envelope text-brand-400 shrink-0 text-sm" />
                <p>
                  <span className="font-semibold text-white mr-1">E-mail:</span>
                  <a href={`mailto:${clinicInfo.email}`} className="text-brand-200 hover:text-white hover:underline">
                    {clinicInfo.email}
                  </a>
                </p>
              </div>
              
              {/* Akut Vagtordning */}
              <div className="border-t border-brand-900 pt-3 space-y-1">
                <span className="font-semibold text-white block uppercase text-[10px] tracking-wider">Akut vagtordning</span>
                <p className="text-[11px] text-brand-400 leading-relaxed">
                  Ring kl. 9-11 i weekender/helligdage på tlf:{" "}
                  <a href={`tel:${clinicInfo.vagtordning.phone.replace(/\s+/g, "")}`} className="text-brand-200 hover:text-white font-semibold">
                    {clinicInfo.vagtordning.phone.replace(/(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4")}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-brand-900 mt-16 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-brand-400">
          <p>© {currentYear} {clinicInfo.name}. Alle rettigheder forbeholdes.</p>
          <div className="flex gap-6">
            <Link to="/kvalitetssikring" className="hover:text-white transition-colors">Datasikkerhed</Link>
            <Link to="/praktisk" className="hover:text-white transition-colors">Priser & Tilskud</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
